<template>
  <div>
    <section class="activity-stage fill-blank-sentences activity-container">
      <aside>
        <figure
          v-for="step of currentRound.steps?.filter(step => step.stepImage)"
          :key="step.stepImage.id"
          :class="`image id-${step.stepImage.id}`"
        >
          <img :src="step.stepImage?.image.src" />
        </figure>
      </aside>
      <div :class="{ 'text-display-lists-container': true, isMobile }">
        <text-display-list
          v-for="(steps, stepIndex) of currentRound.chunckedSteps"
          :key="stepIndex"
        >
          <li v-for="({ id, answer }, index) of steps" :class="[`step answer-${id}`]" :key="index">
            <blinking-cursor-text
              :text="answer"
              :text-step="2 * stepIndex + index"
            ></blinking-cursor-text>
          </li>
        </text-display-list>
      </div>
    </section>
    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';
import _ from 'lodash';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';

export default {
  components: {
    BlinkingCursorText,
    TextDisplayList
  },
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],
  data() {
    return {
      activityName: 'FillBlankSentences'
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();

      this.createRounds(roundSetup);
      await this.loadImages();
    },

    async loadImages() {
      this.rounds.forEach(({ steps, ...round }) => {
        steps
          .filter(({ stepImage }) => stepImage)
          .forEach(({ stepImage }) => {
            this.queueImage(stepImage.image.src);
          });
      });

      return this.downloadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds: rounds.map(({ steps, ...rest }) => ({
          ...rest,
          chunckedSteps: _.chunk(steps, 2),
          steps
        })),
        instructions: {
          voice: `${this.voiceInstructionsPrefix}/bitsaboutit-${this.getRandomVoice()}-1.mp3`,
          wriiten: 'Type every sentence.',
          delay: 4
        }
      };
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setContainerHeight);
      this.$nextTick(() => {
        this.hideAllSteps();
        this.fadeInStep(this.currentStep.id);
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
      });
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();

      if (this.isEndOfCurrentSentence) {
        this.handleStepEnd();
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    handleStepEnd() {
      this.finishStep();
      this.fadeInStep(this.currentStep.id);
    },

    finishStep() {
      this.completeStep();
      this.sndBell();
      if (this.currentStep.stepImage) {
        this.fadeInImage(this.currentStep.stepImage.id);
      }
      this.setCurrentStep();
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();
      if (this.isEndOfActivity) {
        timeline.delay(1).call(this.completeActivity);
      }
    },

    fadeInStep(answerTextId) {
      TweenMax.to(`.step.answer-${answerTextId}`, 0.5, { opacity: 1 });
    },

    fadeInImage(stepImageId) {
      TweenMax.to(`.image.id-${stepImageId}`, 0.5, { opacity: 1 });
    },

    hideAllSteps() {
      TweenMax.set('.step', { opacity: 0 });
      TweenMax.set('.image', { opacity: 0 });
    },

    setContainerHeight() {
      const keyboard = document.querySelector('.phoenix-keyboard-wrapper');
      const mainContainer = document.querySelector('.main-container');
      const blockContainer = document.querySelector('.activity-container');

      blockContainer.style.height = `calc(${
        mainContainer.clientHeight
      }px - ${keyboard.clientHeight + 10}px)`;
    }
  }
};
</script>

<style lang="scss" scoped>
.activity-stage {
  opacity: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  ul {
    list-style: none;
    align-self: center;
    margin: 0;
    padding: 0;
    font-size: 22px;

    .title {
      font-weight: bold;
      margin-bottom: 10px;
    }

    .step {
      display: flex;
      flex: 1;
      justify-content: flex-start;

      &.padded {
        padding-top: 40px;
      }
    }
  }
  aside {
    align-items: center;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-around;
    min-height: 310px;
    min-width: 30%;

    figure {
      margin: 0 auto;
    }
  }
  .text-display-lists-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-around;

    &.isMobile {
      padding-right: 63px;
    }
  }
}
</style>
